import { FunctionComponent, useRef } from "react";

import { FilterBar } from "@components/filter-bar/FilterBar";
import { FilterBarProps, Item } from "@components/filter-bar/FilterBar.types";
import { BpIdAuditLogArgs } from "@libs/api/gateways/bp-id/bp-id.dtos";
import { useApplicationsRefData } from "@libs/api/gateways/plt/plt-gateway.hooks";

interface BpIdAuditLogsFilterProps
  extends Pick<FilterBarProps<BpIdAuditLogArgs>, "children"> {
  // Override the default filter values. Pass {} to start with no date range
  // (the default is the last 7 days).
  initialValues?: BpIdAuditLogArgs;
}

export const BpIdAuditLogsFilter: FunctionComponent<BpIdAuditLogsFilterProps> = ({
  initialValues: initialValuesOverride,
  ...props
}) => {
  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - 7);
  const initialValues = useRef<BpIdAuditLogArgs>(
    initialValuesOverride ?? {
      startDate: defaultStartDate,
      endDate: new Date()
    }
  );

  const { data: applicationCodes } = useApplicationsRefData();

  const auditLogSeverities: string[] = [
    "Information",
    "Warning",
    "Debug",
    "Error"
  ];

  // Keep in sync with the backend Bps.Platform.BpId.Audit.System constants:
  // AuditLogEventSource / AuditLogEventType.
  const auditLogSources: string[] = [
    "OperationsApi",
    "SystemApi",
    "B2C",
    "SiteIntegration"
  ];

  const auditLogEventTypes: string[] = [
    "Invitation",
    "InvitationSettings",
    "Login",
    "Signout",
    "LoginOptions",
    "LoginSettings",
    "MFA",
    "MFA-Authenticator",
    "MFA-Phone",
    "MFA-Email",
    "MFARemoveInvalidMethod"
  ];

  const items: Item<BpIdAuditLogArgs>[] = [
    {
      type: "datesRangePicker",
      name: "datesRangePicker",
      stickItem: true,
      props: {
        id: "usersigninlogs-filter-dates-range",
        startDateProps: { id: "usersigninlogs-filter-start-date" },
        endDateProps: { id: "usersigninlogs-filter-end-date" },
        startDateName: "startDate",
        endDateName: "endDate",
        placeholder: "Date(s)"
      }
    },
    {
      type: "optionsSelect",
      name: "severity",
      props: {
        placeholder: "Severity",
        hideSearchOption: true,
        multiSelect: true,
        options: auditLogSeverities.map(x => ({ key: x, text: x }))
      }
    },
    {
      type: "optionsSelect",
      name: "source",
      props: {
        placeholder: "Source",
        hideSearchOption: true,
        multiSelect: true,
        options: auditLogSources.map(x => ({ key: x, text: x }))
      }
    },
    {
      type: "optionsSelect",
      name: "eventType",
      props: {
        placeholder: "Event Type",
        hideSearchOption: true,
        multiSelect: true,
        options: auditLogEventTypes.map(x => ({ key: x, text: x }))
      }
    },
    {
      type: "optionsSelect",
      name: "applicationCode",
      props: {
        placeholder: "Applications",
        hideSearchOption: true,
        multiSelect: true,
        options:
          applicationCodes?.map(x => ({ key: x.code, text: x.text })) ?? []
      }
    }
  ];

  return (
    <FilterBar {...props} items={items} initialValues={initialValues.current} />
  );
};
